import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'https://api.innoworx.ai/Stage';

class LeaveApiService {
  async getAuthHeaders() {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      };
    } catch (error) {
      console.error('Error getting auth token:', error);
      throw new Error('Authentication failed');
    }
  }

  async getCurrentUserInfo() {
    try {
      const response = await this.makeRequest('/user/me');
      return response;
    } catch (error) {
      console.error('Error getting current user info:', error);
      return null;
    }
  }

  async getCurrentUserId() {
    try {
      const userInfo = await this.getCurrentUserInfo();
      return userInfo?.user_id || null;
    } catch (error) {
      console.error('Error getting current user ID:', error);
      return null;
    }
  }

  async getUserInfo(userId) {
    try {
      if (!userId) {
        return await this.getCurrentUserInfo();
      }

      // Use the /user endpoint with user_id parameter
      const params = new URLSearchParams({ user_id: userId });
      const response = await this.makeRequest(`/user?${params}`);
      return response;
    } catch (error) {
      console.error('Error getting user info:', error);
      return null;
    }
  }

  async makeRequest(endpoint, options = {}) {
    const headers = await this.getAuthHeaders();
    const url = `${API_BASE_URL}${endpoint}`;
    const method = options.method || 'GET';

    const config = {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        const error = new Error(
          data.error || `HTTP ${response.status}: ${response.statusText}`
        );
        error.status = response.status;
        error.statusText = response.statusText;
        error.endpoint = endpoint;
        error.responseData = data;
        
        // Log API error to logging service
        loggingService.logError(
          'leaveApiError',
          `Leave API error: ${response.status} ${response.statusText} for ${endpoint}`,
          error,
          {
            endpoint,
            method,
            status: response.status,
            statusText: response.statusText,
            responseData: data,
          }
        ).catch(logError => {
          console.warn('[leaveApi] Failed to log error:', logError);
        });
        
        throw error;
      }

      return data;
    } catch (error) {
      // Log network/fetch errors to logging service
      if (error.status === undefined) {
        console.error(`API request failed for ${endpoint}:`, error);
        error.endpoint = endpoint;
        error.method = method;
        
        loggingService.logError(
          'leaveApiError',
          `Leave API network/fetch error for ${endpoint}`,
          error,
          {
            endpoint,
            method,
            errorType: error.name || 'NetworkError',
          }
        ).catch(logError => {
          console.warn('[leaveApi] Failed to log error:', logError);
        });
      }
      
      throw error;
    }
  }

  // Create a new leave request
  async createLeaveRequest(leaveData) {
    const { dateList, leaveType, numberOfDays, reason } = leaveData;

    return this.makeRequest('/leave', {
      method: 'POST',
      body: JSON.stringify({
        date_list: dateList,
        leave_type: leaveType,
        number_of_days: numberOfDays,
        reason: reason,
      }),
    });
  }

  // Get all leave requests for the current user
  // Lambda: innoworx-getall-my-leave-list
  async getMyLeaveRequests(userId = null) {
    // Note: Lambda function uses Cognito claims directly, no user_id parameter needed
    return this.makeRequest('/leave');
  }

  // Get pending leave requests for team management with pagination
  // Lambda: innoworx-get-leave-request-myteam-pending
  async getPendingLeaveRequests(limit = 10, exclusiveStartKey = null) {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (exclusiveStartKey) {
      params.append('exclusiveStartKey', JSON.stringify(exclusiveStartKey));
    }
    return this.makeRequest(`/leave/manager/pending?${params}`);
  }

  // Get decided leave requests (history)
  // Lambda: innoworx-get-leave-request-myteam-notPending
  async getDecidedLeaveRequests(limit = 10, userId = null) {
    // Note: Lambda function uses Cognito claims directly, no user_id parameter needed
    const params = new URLSearchParams({ limit: limit.toString() });
    return this.makeRequest(`/leave/manager/not-pending?${params}`);
  }

  // Get leave request details by ID
  // Lambda: innoworx-get-leave-request-info
  async getLeaveRequestInfo(requestId, userId = null) {
    // Note: Lambda function uses Cognito claims directly, no user_id parameter needed
    return this.makeRequest(`/leave/${requestId}`);
  }

  // Update leave request status (approve/reject)
  // Lambda: innoworx-update-leave-request
  async updateLeaveRequest(requestId, status, remarks, userId = null) {
    // Note: Lambda function uses Cognito claims directly, no user_id parameter needed
    const body = {
      status: status,
      remarks: remarks,
    };

    return this.makeRequest(`/leave/manager/decision/${requestId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  // Get leave availability information
  // Lambda: innoworx-get-leave-availability-info
  async getLeaveAvailabilityInfo(userId = null, date = null) {
    // Note: Lambda function requires user_id and date parameters
    if (!userId || !date) {
      throw new Error(
        'user_id and date are required for leave availability check'
      );
    }
    const params = new URLSearchParams({
      user_id: userId,
      date: date,
    });
    return this.makeRequest(`/leave/availability?${params}`);
  }

  // Get leave requests for a specific user (useful for managers)
  async getLeaveRequestsForUser(userId) {
    if (!userId) {
      throw new Error('User ID is required for this operation');
    }
    return this.getMyLeaveRequests(userId);
  }

  // Helper method to get user name for display
  async getUserDisplayName(userId = null) {
    try {
      const userInfo = userId
        ? await this.getUserInfo(userId)
        : await this.getCurrentUserInfo();
      if (userInfo) {
        // Try different possible name fields based on the API response structure
        return (
          userInfo.user_name ||
          userInfo.name ||
          userInfo.employee_id ||
          userInfo.user_id ||
          'Unknown User'
        );
      }
      return userId || 'Unknown User';
    } catch (error) {
      console.error('Error getting user display name:', error);
      return userId || 'Unknown User';
    }
  }

  // Get leave balance for current user (using availability endpoint)
  // NOTE: Leave balance is not available in the backend, using default values
  async getLeaveBalance() {
    // Commented out - leave balance not available in backend
    // try {
    //   const response = await this.makeRequest('/leave/availability');
    //   return response.leave_balance || response;
    // } catch (error) {
    //   console.error('Error fetching leave balance:', error);
    //   // Return default balance if API fails
    //   return {
    //     casual_leave: 12,
    //     sick_leave: 7,
    //     earned_leave: 21,
    //     compensatory_off: 0,
    //     loss_of_pay: 0,
    //   };
    // }

    // Return default balance since backend doesn't support this
    return {
      casual_leave: 12,
      sick_leave: 7,
      earned_leave: 21,
      compensatory_off: 0,
      loss_of_pay: 0,
    };
  }

  // Get leave types configuration (using availability endpoint)
  // NOTE: Leave types configuration is not available in the backend, using default values
  async getLeaveTypesConfig() {
    // Commented out - leave types not available in backend
    // try {
    //   const response = await this.makeRequest('/leave/availability');
    //   return response.leave_types || this.getLeaveTypes();
    // } catch (error) {
    //   console.error('Error fetching leave types:', error);
    //   return this.getLeaveTypes();
    // }

    // Return default leave types since backend doesn't support this
    return this.getLeaveTypes();
  }

  // Helper method to format dates for API (preserves local date)
  formatDateForApi(date) {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      return '';
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`; // YYYY-MM-DD format in local timezone
  }

  // Helper method to parse API dates
  parseApiDate(dateString) {
    return new Date(dateString);
  }

  // Helper method to format dates for display
  formatDateForDisplay(date) {
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date);
  }

  // Helper method to get leave type options
  getLeaveTypes() {
    return [
      { value: 'Casual Leave (CL)', label: 'Casual Leave (CL)' },
      { value: 'Sick Leave (SL)', label: 'Sick Leave (SL)' },
      { value: 'Earned Leave (EL)', label: 'Earned Leave (EL)' },
      { value: 'Compensatory Off (CO)', label: 'Compensatory Off (CO)' },
      { value: 'Loss of Pay Leave (LOP)', label: 'Loss of Pay Leave (LOP)' },
    ];
  }

  // Helper method to get status display info
  getStatusInfo(status) {
    const statusMap = {
      pending: { color: '#3B82F6', label: 'Pending', icon: '⏳' },
      approved: { color: '#10B981', label: 'Approved', icon: '✅' },
      rejected: { color: '#EF4444', label: 'Rejected', icon: '❌' },
    };
    return (
      statusMap[status] || { color: '#6B7280', label: 'Unknown', icon: '❓' }
    );
  }

  // Get role information by spec_role
  async getRoleInfo(specRole) {
    try {
      if (!specRole) return null;
      const response = await this.makeRequest(`/user/roles/${specRole}`);
      return response;
    } catch (error) {
      console.error('Error getting role info:', error);
      return null;
    }
  }
}

export default new LeaveApiService();